import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Loader2, RotateCcw } from 'lucide-react';
import { useI18n } from '../../../contexts/I18nContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useLeaveBalance, useLeaveRequests, useSetLeaveEntitlement } from '../../../hooks/useLeave';
import {
  LEAVE_STATUS_COLOR,
  LEAVE_TYPES,
  LEAVE_TYPE_COLOR,
  formatLeaveLength,
  formatLeaveRange,
  leaveStatusLabel,
  leaveTypeLabel,
} from '../../../lib/leaveConstants';
import type { LeaveBalance, LeaveRequest } from '../../../lib/leaveApi';
import { SkeletonList, SkeletonStats } from '../../ui/Skeleton';
import { useToast } from '../../tasks/linear/Toast';
import { PendingTab } from './PendingTab';

// ---------------------------------------------------------------------------
// "Concedii" tab on the person detail page (Echipă).
//
// One person's leave for one year: the yearly balance, the per-type breakdown
// and the request history. Managers (`leave.canManage`) can also set this
// person's yearly entitlement here — this tab is the only place the house
// default can be overridden, which is what `leave.balance.default` points at.
//
// Read/write permissions are enforced server-side; the page itself only hides
// what the caller cannot use.
// ---------------------------------------------------------------------------

export interface PersonLeaveTabProps {
  /** Person.id — the entitlement is stored on the person, not on the account. */
  personId: string;
  /** Person.userId — the linked login account. Null when the person has none. */
  userId: string | null;
}

/** The years offered in the selector: a few back, plus next year for planning. */
function selectableYears(current: number): number[] {
  return [current + 1, current, current - 1, current - 2];
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${accent ?? 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

function EntitlementEditor({ personId, balance }: { personId: string; balance: LeaveBalance }) {
  const { t } = useI18n();
  const toast = useToast();
  const setEntitlement = useSetLeaveEntitlement();
  const [days, setDays] = useState(String(balance.entitledDays));

  // Re-sync when the server number changes (saved here, or a different year).
  useEffect(() => {
    setDays(String(balance.entitledDays));
  }, [balance.entitledDays, balance.year]);

  async function save(value: number | null) {
    try {
      await setEntitlement.mutateAsync({ personId, days: value });
    } catch {
      toast.show(t('leave.error'));
    }
  }

  const parsed = Number(days);
  const canSave =
    days.trim() !== '' &&
    Number.isInteger(parsed) &&
    parsed >= 0 &&
    parsed !== balance.entitledDays;

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <label
        htmlFor="person-leave-entitlement"
        className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500"
      >
        {t('leave.entitlement')}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          id="person-leave-entitlement"
          type="number"
          min={0}
          max={365}
          step={1}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          disabled={setEntitlement.isPending}
          className="ui-input sm:w-28"
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="ui-btn-primary"
            disabled={!canSave || setEntitlement.isPending}
            onClick={() => void save(parsed)}
          >
            {setEntitlement.isPending && <Loader2 size={14} className="animate-spin" />}
            {t('leave.entitlementSave')}
          </button>
          <button
            type="button"
            className="ui-btn-secondary"
            disabled={setEntitlement.isPending || balance.isDefaultEntitlement}
            onClick={() => void save(null)}
          >
            <RotateCcw size={14} /> {t('leave.entitlementReset')}
          </button>
        </div>
      </div>
    </div>
  );
}

function BalanceCard({
  personId,
  balance,
  canManage,
}: {
  personId: string;
  balance: LeaveBalance;
  canManage: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900">
        {t('leave.balance.title', { year: String(balance.year) })}
      </h3>
      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label={t('leave.balance.entitled')} value={String(balance.entitledDays)} />
        <Stat label={t('leave.balance.used')} value={String(balance.usedDays)} />
        <Stat
          label={t('leave.balance.pending')}
          value={String(balance.pendingDays)}
          accent={balance.pendingDays > 0 ? 'text-amber-600' : undefined}
        />
        <Stat
          label={t('leave.balance.remaining')}
          value={String(balance.remainingDays)}
          accent={balance.remainingDays <= 0 ? 'text-red-600' : 'text-green-700'}
        />
      </div>
      <p className="mt-3 text-xs text-gray-500">{t('leave.balance.hint')}</p>
      {balance.isDefaultEntitlement && (
        <p className="mt-1 text-xs text-gray-400">{t('leave.balance.default')}</p>
      )}
      {canManage && <EntitlementEditor personId={personId} balance={balance} />}
    </div>
  );
}

function TypeBreakdown({ balance }: { balance: LeaveBalance }) {
  const { t } = useI18n();
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900">Pe tipuri de concediu</h3>
      <ul className="mt-3 space-y-2">
        {LEAVE_TYPES.map((type) => {
          const value = balance.byType[type] ?? 0;
          return (
            <li key={type} className="flex items-center justify-between gap-3 text-sm">
              <span className="flex min-w-0 items-center gap-2">
                <span
                  aria-hidden="true"
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: LEAVE_TYPE_COLOR[type] }}
                />
                <span className="truncate text-gray-700">{leaveTypeLabel(type)}</span>
              </span>
              <span className="shrink-0 font-medium text-gray-900">
                {type === 'SHORT_ABSENCE'
                  ? t('leave.lengthHours', { count: String(value) })
                  : t(value === 1 ? 'leave.lengthDay' : 'leave.lengthDays', {
                      count: String(value),
                    })}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RequestRow({ request, year }: { request: LeaveRequest; year: number }) {
  const { t, locale } = useI18n();
  const intlLocale = locale === 'ro' ? 'ro-RO' : 'en-US';
  return (
    <li className="flex flex-col gap-1.5 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: LEAVE_TYPE_COLOR[request.type] }}
          />
          <span className="truncate">{leaveTypeLabel(request.type)}</span>
        </p>
        <p className="mt-0.5 text-sm text-gray-600">
          {formatLeaveRange(request.startDate, request.endDate, intlLocale, year)} ·{' '}
          {formatLeaveLength(request.type, request.workingDays, request.hours)}
        </p>
        {request.reason && <p className="mt-0.5 text-xs text-gray-500">{request.reason}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end">
        <span
          className="ui-badge"
          style={{
            backgroundColor: `${LEAVE_STATUS_COLOR[request.status]}1f`,
            color: LEAVE_STATUS_COLOR[request.status],
          }}
        >
          {leaveStatusLabel(request.status)}
        </span>
        {request.decidedBy && (
          <span className="text-xs text-gray-400">
            {t('leave.decidedBy', { name: request.decidedBy.name })}
          </span>
        )}
      </div>
    </li>
  );
}

export function PersonLeaveTab({ personId, userId }: PersonLeaveTabProps) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const canManage = can('leave', 'canManage');
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const hasAccount = Boolean(userId);
  const balanceQuery = useLeaveBalance(userId ?? undefined, year, hasAccount);
  const requestsQuery = useLeaveRequests(
    { userId: userId ?? undefined, from: `${year}-01-01`, to: `${year}-12-31`, pageSize: 100 },
    hasAccount,
  );

  const requests = useMemo(() => requestsQuery.data?.requests ?? [], [requestsQuery.data]);
  const years = useMemo(() => selectableYears(currentYear), [currentYear]);

  if (!userId) {
    return (
      <PendingTab
        title="Concedii"
        description="Acest membru al echipei nu are un cont de autentificare asociat, așa că nu poate depune cereri de concediu. Creează-i un cont pentru a-i urmări zilele de concediu aici."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
          <CalendarDays size={16} className="text-gray-400" /> Concedii
        </h2>
        <label className="flex items-center gap-2 text-sm text-gray-500">
          <span>Anul</span>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="ui-select w-28"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      {balanceQuery.isLoading ? (
        <SkeletonStats count={4} className="grid grid-cols-2 gap-3 lg:grid-cols-4" />
      ) : balanceQuery.isError || !balanceQuery.data ? (
        <p className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500 shadow-sm">
          {t('leave.noAccess')}
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <BalanceCard personId={personId} balance={balanceQuery.data} canManage={canManage} />
          <TypeBreakdown balance={balanceQuery.data} />
        </div>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-900">Cereri în {year}</h3>
        {requestsQuery.isLoading ? (
          <SkeletonList rows={4} badge />
        ) : requestsQuery.isError ? (
          <p className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-red-600 shadow-sm">
            {t('leave.error')}
          </p>
        ) : requests.length === 0 ? (
          <p className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
            Nicio cerere de concediu în {year}.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white shadow-sm">
            {requests.map((request) => (
              <RequestRow key={request.id} request={request} year={year} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
